import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../ui/dialog';
import { Plus, Trash2, ShoppingCart, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';

import SyndicationSummary from './SyndicationSummary';
import SyndicatorList from './SyndicatorList';
import DistributionTable from './DistributionTable';
import CofundedOwedCard from './CofundedOwedCard';   // 10/20: PFS / DIB owed to the co-funded partner
import { WireInPreview, isPfsName } from '../syndication/WireInPreview';   // 10/28: shared with Needs Syndication

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ROLE_LABELS = { PARTNER: 'Partner', INVESTOR_FEE: 'Investor (entitlement)', INVESTOR: 'Investor' };
const ROLE_COLORS = {
  PARTNER: 'bg-purple-100 text-purple-700',
  INVESTOR_FEE: 'bg-blue-100 text-blue-700',
  INVESTOR: 'bg-slate-100 text-slate-600',
};

// ─── Cart Dialog ─────────────────────────────────────────────────────────
const CartDialog = ({ investors, syndications, cart, setCart, cartForm, setCartForm, saving, onCheckout, onClose, dealFinancials }) => {
  const [existingEdits, setExistingEdits] = useState(() =>
    (syndications || []).map(s => {
      const inv = investors.find(i => i.id === s.syndicator_id);
      return { id: s.id, syndicator_id: s.syndicator_id, deal_id: s.deal_id, name: inv?.company_name || s.syndicator_id, _pct: (s.ownership_percentage || 0).toString(), _role: s.role || 'INVESTOR', _deleted: false, _origPct: s.ownership_percentage || 0, _origRole: s.role || 'INVESTOR' };
    })
  );
  const existingPct = useMemo(() => existingEdits.filter(e => !e._deleted).reduce((sum, e) => sum + (parseFloat(e._pct) || 0), 0), [existingEdits]);
  const cartPct = useMemo(() => cart.reduce((sum, item) => sum + (parseFloat(item.ownership_percentage) || 0), 0), [cart]);
  const totalPct = existingPct + cartPct;
  const remaining = Math.max(0, 100 - totalPct);
  const existingActiveIds = useMemo(() => {
    const ids = new Set(existingEdits.filter(e => !e._deleted).map(e => e.syndicator_id));
    cart.forEach(c => ids.add(c.syndicator_id));
    return ids;
  }, [existingEdits, cart]);
  const availableInvestors = investors.filter(i => !existingActiveIds.has(i.id));
  const investorName = (id) => investors.find(i => i.id === id)?.company_name || id;
  // Josh 10/01: every syndicator pays the 2% on an ISO deal, any role — only PFS's own contact is exempt
  const isPfs = (id) => isPfsName(investors.find(i => i.id === id)?.company_name);
  const updateExisting = (sid, field, value) => setExistingEdits(prev => prev.map(e => e.syndicator_id === sid ? { ...e, [field]: value } : e));
  const toggleDelete = (sid) => setExistingEdits(prev => prev.map(e => e.syndicator_id === sid ? { ...e, _deleted: !e._deleted } : e));
  const handleAddToCart = () => {
    if (!cartForm.syndicator_id || !cartForm.ownership_percentage) { toast.error('Select an investor and enter %'); return; }
    const pct = parseFloat(cartForm.ownership_percentage);
    if (pct <= 0 || pct > 100) { toast.error('% must be between 0 and 100'); return; }
    if (totalPct + pct > 100) { toast.error(`Only ${remaining.toFixed(2)}% remaining`); return; }
    setCart([...cart, { ...cartForm, ownership_percentage: pct }]);
    setCartForm({ syndicator_id: '', ownership_percentage: '', role: 'INVESTOR', management_fee_pct: '' });
  };
  const handleRemoveFromCart = (idx) => setCart(cart.filter((_, i) => i !== idx));
  const deletedCount = existingEdits.filter(e => e._deleted).length;
  const changedCount = existingEdits.filter(e => !e._deleted && (parseFloat(e._pct) !== e._origPct || e._role !== e._origRole)).length;
  const totalChanges = deletedCount + changedCount + cart.length;
  const pctColor = totalPct === 100 ? 'text-emerald-600' : totalPct > 100 ? 'text-red-600' : 'text-amber-600';
  const barColor = totalPct === 100 ? 'bg-emerald-500' : totalPct > 100 ? 'bg-red-500' : 'bg-blue-500';

  return (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2"><ShoppingCart className="h-4 w-4" /> Manage Syndicators</DialogTitle>
        <DialogDescription className="text-xs text-gray-500">Edit existing syndicators or add new ones below.</DialogDescription>
      </DialogHeader>
      <div className="space-y-4 max-h-[65vh] overflow-y-auto pr-1">
        <div className="space-y-1" data-testid="pct-counter">
          <div className="flex justify-between text-xs"><span className="text-gray-500">Allocation</span><span className={`font-bold ${pctColor}`}>{totalPct.toFixed(2)}% / 100%</span></div>
          <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
            {existingPct > 0 && <div className="h-full bg-gray-300 float-left" style={{ width: `${Math.min(existingPct, 100)}%` }} />}
            <div className={`h-full ${barColor} float-left transition-all`} style={{ width: `${Math.min(cartPct, 100 - existingPct)}%` }} />
          </div>
          <div className="flex justify-between text-[10px] text-gray-400"><span>Existing: {existingPct.toFixed(2)}%</span><span>New: +{cartPct.toFixed(2)}%</span><span>Remaining: {remaining.toFixed(2)}%</span></div>
        </div>
        {existingEdits.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Current Syndicators</p>
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm" data-testid="existing-syndicators-table">
                <thead className="bg-gray-50 text-xs text-gray-500"><tr><th className="text-left px-3 py-2 font-medium">Name</th><th className="text-right px-3 py-2 font-medium w-24">%</th><th className="text-center px-3 py-2 font-medium w-36">Role</th><th className="w-10"></th></tr></thead>
                <tbody>
                  {existingEdits.map((e) => {
                    const changed = !e._deleted && (parseFloat(e._pct) !== e._origPct || e._role !== e._origRole);
                    return (
                      <tr key={e.syndicator_id} className={`border-t transition-colors ${e._deleted ? 'bg-red-50 opacity-50' : changed ? 'bg-amber-50' : ''}`} data-testid={`existing-row-${e.syndicator_id}`}>
                        <td className="px-3 py-2 font-medium"><span className={e._deleted ? 'line-through text-gray-400' : ''}>{e.name}</span>{changed && <span className="ml-1.5 text-[10px] text-amber-600 font-semibold">edited</span>}</td>
                        <td className="px-3 py-2"><input type="number" step="0.01" min="0" max="100" value={e._pct} onChange={(ev) => updateExisting(e.syndicator_id, '_pct', ev.target.value)} disabled={e._deleted} className="w-16 text-right border rounded px-1.5 py-0.5 text-xs font-mono disabled:bg-gray-50 disabled:text-gray-400" data-testid={`existing-pct-${e.syndicator_id}`} /></td>
                        <td className="px-3 py-2 text-center"><select value={e._role} onChange={(ev) => updateExisting(e.syndicator_id, '_role', ev.target.value)} disabled={e._deleted} className="rounded border border-gray-200 px-1.5 text-xs h-7 disabled:bg-gray-50 disabled:text-gray-400" data-testid={`existing-role-${e.syndicator_id}`}><option value="PARTNER">Partner</option><option value="INVESTOR_FEE">Inv (Fee)</option><option value="INVESTOR">Investor</option></select></td>
                        <td className="px-3 py-2"><button onClick={() => toggleDelete(e.syndicator_id)} className={e._deleted ? 'text-gray-400 hover:text-gray-700' : 'text-red-400 hover:text-red-600'} title={e._deleted ? 'Undo remove' : 'Remove syndicator'} data-testid={`existing-delete-${e.syndicator_id}`}>{e._deleted ? <RotateCcw className="h-3.5 w-3.5" /> : <Trash2 className="h-3.5 w-3.5" />}</button></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">{existingEdits.length > 0 ? 'Add New Syndicator' : 'Add Syndicator'}</p>
          <div className="flex gap-2 items-end">
            <div className="flex-1"><Label className="text-xs">Investor</Label><Select value={cartForm.syndicator_id} onValueChange={(v) => setCartForm({ ...cartForm, syndicator_id: v, role: investors.find(i => i.id === v)?.collect_fees === true ? 'INVESTOR_FEE' : cartForm.role })}><SelectTrigger className="h-9" data-testid="cart-investor-select"><SelectValue placeholder="Select investor" /></SelectTrigger><SelectContent>{availableInvestors.map(inv => <SelectItem key={inv.id} value={inv.id}>{inv.company_name}</SelectItem>)}</SelectContent></Select></div>
            <div className="w-20"><Label className="text-xs">%</Label><Input type="number" step="0.01" min="0" max={remaining} value={cartForm.ownership_percentage} onChange={(e) => setCartForm({ ...cartForm, ownership_percentage: e.target.value })} className="h-9" placeholder={`≤${remaining.toFixed(1)}`} data-testid="cart-pct-input" /></div>
            <div><Label className="text-xs">Role</Label><select value={cartForm.role} onChange={(e) => setCartForm({ ...cartForm, role: e.target.value })} className="h-9 rounded border border-gray-200 px-1.5 text-xs w-32" data-testid="cart-role-select"><option value="PARTNER">Partner</option><option value="INVESTOR_FEE">Inv (Fee)</option><option value="INVESTOR">Investor</option></select></div>
            <div className="w-24"><Label className="text-xs" title="Management fee (2% upcharge) — asked every time; charged up front on its own line">Mgmt fee %</Label><Input type="number" step="0.01" min="0" value={cartForm.management_fee_pct} onChange={(e) => setCartForm({ ...cartForm, management_fee_pct: e.target.value })} className="h-9" placeholder={dealFinancials?.is_iso_originated && !isPfs(cartForm.syndicator_id) ? '2 (ISO deal)' : '0'} data-testid="cart-mgmt-fee-input" /></div>
            <Button size="sm" onClick={handleAddToCart} disabled={!cartForm.syndicator_id || !cartForm.ownership_percentage} className="h-9 px-3 mt-4" data-testid="add-to-cart-btn"><Plus className="h-4 w-4" /></Button>
          </div>
          {cartForm.ownership_percentage && parseFloat(cartForm.ownership_percentage) > 0 && cartForm.role && (
            <div className="mt-2"><WireInPreview role={cartForm.role} ownershipPct={parseFloat(cartForm.ownership_percentage)} dealFinancials={dealFinancials} managementFeePct={cartForm.management_fee_pct === '' ? (dealFinancials?.is_iso_originated && !isPfs(cartForm.syndicator_id) ? 2 : 0) : cartForm.management_fee_pct} /></div>
          )}
        </div>
        {cart.length > 0 && (
          <div className="border rounded-lg overflow-hidden" data-testid="cart-items">
            <table className="w-full text-sm">
              <thead className="bg-green-50 text-xs text-gray-500"><tr><th className="text-left px-3 py-2 font-medium">New Investor</th><th className="text-right px-3 py-2 font-medium w-20">%</th><th className="text-center px-3 py-2 font-medium w-28">Role</th><th className="w-10"></th></tr></thead>
              <tbody>
                {cart.map((item, idx) => (
                  <tr key={idx} className="border-t bg-green-50/40">
                    <td className="px-3 py-2">{investorName(item.syndicator_id)}</td>
                    <td className="px-3 py-2 text-right font-mono">{item.ownership_percentage}%</td>
                    <td className="px-3 py-2 text-center"><span className={`text-xs px-1.5 py-0.5 rounded ${ROLE_COLORS[item.role] || ROLE_COLORS.INVESTOR}`}>{ROLE_LABELS[item.role] || item.role}</span></td>
                    <td className="px-3 py-2"><button onClick={() => handleRemoveFromCart(idx)} className="text-red-400 hover:text-red-600" data-testid={`cart-remove-${idx}`}><Trash2 className="h-3.5 w-3.5" /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <div className="flex justify-between items-center pt-3 border-t mt-2">
        <div className="flex items-center gap-3 text-xs">
          {deletedCount > 0 && <span className="text-red-600 font-medium">{deletedCount} removed</span>}
          {changedCount > 0 && <span className="text-amber-600 font-medium">{changedCount} updated</span>}
          {cart.length > 0 && <span className="text-green-600 font-medium">{cart.length} new</span>}
          {totalChanges === 0 && <span className="text-gray-400">No changes yet</span>}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onClose} size="sm">Cancel</Button>
          <Button onClick={() => onCheckout(existingEdits, cart)} disabled={totalChanges === 0 || saving} size="sm" data-testid="cart-checkout-btn">{saving ? 'Saving...' : `Save Changes${totalChanges > 0 ? ` (${totalChanges})` : ''}`}</Button>
        </div>
      </div>
    </>
  );
};

// ─── Main SyndicationTab ─────────────────────────────────────────────────
const SyndicationTab = ({
  syndications, investors, syndicationDialogOpen, setSyndicationDialogOpen,
  syndicationForm, setSyndicationForm, editingSyndication, setEditingSyndication,
  onAddSyndication, onDeleteSyndication, onRefetch, isAdmin, dealId, getAuthHeaders,
}) => {
  const [allocations, setAllocations] = useState(null);
  const [loading, setLoading] = useState(false);
  const [recomputing, setRecomputing] = useState(false);
  const [cart, setCart] = useState([]);
  const [cartForm, setCartForm] = useState({ syndicator_id: '', ownership_percentage: '', role: 'INVESTOR', management_fee_pct: '' });
  const [saving, setSaving] = useState(false);
  const [dealFinancials, setDealFinancials] = useState({ funding_amount: 0, origination_fee: 0, broker_commission: 0, is_iso_originated: false });

  useEffect(() => {
    if (!dealId || !getAuthHeaders) return;
    (async () => {
      try {
        const res = await axios.get(`${API}/deals/${dealId}`, { headers: getAuthHeaders() });
        const d = res.data;
        setDealFinancials({ funding_amount: d.funding_amount || 0, origination_fee: d.fees || 0, broker_commission: d.broker_commission || 0, payback: d.payback || 0, is_iso_originated: d.is_iso_originated === true, management_fee_rate: d.management_fee_rate });
      } catch { /* ignore */ }
    })();
  }, [dealId, getAuthHeaders]);

  const fetchAllocations = useCallback(async () => {
    if (!dealId || !getAuthHeaders) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API}/syndications/deal/${dealId}/allocations`, { headers: getAuthHeaders() });
      setAllocations(response.data);
    } catch (error) {
      console.error('Failed to fetch allocations:', error);
      setAllocations(null);
    } finally { setLoading(false); }
  }, [dealId, getAuthHeaders]);

  useEffect(() => { fetchAllocations(); }, [fetchAllocations, syndications]);

  const handleRecompute = async () => {
    if (!isAdmin) return;
    setRecomputing(true);
    try {
      await axios.post(`${API}/syndications/recompute/deal/${dealId}`, {}, { headers: { ...getAuthHeaders(), 'X-Tenant': 'live' } });
      toast.success('Syndications recomputed successfully');
      fetchAllocations();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to recompute syndications'); }
    finally { setRecomputing(false); }
  };

  const handleCartCheckout = async (existingEdits, newCart) => {
    setSaving(true);
    let deleted = 0, updated = 0, created = 0;
    try {
      for (const e of (existingEdits || []).filter(e => e._deleted)) {
        const delRes = await axios.delete(`${API}/syndications/${e.id}`, { headers: getAuthHeaders(), params: { confirmation: 'DELETE', reason: 'Removed via Manage Syndicators dialog' } });
        deleted++;
        // 10/23: the delete works backwards (commitment ledger entry, offer state) — say what else went
        if (delRes.data?.cascade && (delRes.data.cascade.commitment_entries_deleted || delRes.data.cascade.offer_reopened)) toast.info(delRes.data.message);
      }
      for (const e of (existingEdits || []).filter(e => !e._deleted && (parseFloat(e._pct) !== e._origPct || e._role !== e._origRole))) {
        await axios.put(`${API}/syndications/${e.id}`, { deal_id: dealId, syndicator_id: e.syndicator_id, ownership_percentage: parseFloat(e._pct), role: e._role }, { headers: getAuthHeaders() });
        updated++;
      }
      let lastRetro = null;
      for (const item of (newCart || [])) {
        const response = await axios.post(`${API}/syndications`, { deal_id: dealId, syndicator_id: item.syndicator_id, ownership_percentage: item.ownership_percentage, role: item.role || 'INVESTOR',
          management_fee_pct: item.management_fee_pct === '' || item.management_fee_pct == null ? null : parseFloat(item.management_fee_pct) }, { headers: getAuthHeaders() });
        created++;
        if (response.data.retroactive_applied && response.data.retroactive_amount > 0) lastRetro = response.data.retroactive_amount;
      }
      const parts = [];
      if (deleted > 0) parts.push(`${deleted} removed`);
      if (updated > 0) parts.push(`${updated} updated`);
      if (created > 0) parts.push(`${created} added`);
      if (lastRetro) toast.success('Syndicators saved! Retroactive credits applied.', { duration: 5000 });
      else toast.success(`Syndicators updated: ${parts.join(', ')}`);
      setCart([]);
      setSyndicationDialogOpen(false);
      if (onRefetch) onRefetch();
    } catch (error) {
      const done = deleted + updated + created;
      if (done > 0) toast.info(`${done} change(s) saved before error`);
      const detail = error.response?.data?.detail;
      const errorMsg = typeof detail === 'string' ? detail : Array.isArray(detail) ? (detail[0]?.msg || JSON.stringify(detail)) : 'Failed to update syndicators';
      toast.error(errorMsg);
    } finally { setSaving(false); }
  };

  const handleEditSyndication = (syndication) => {
    const allocationData = allocations?.allocations?.find(a => a.syndicator_id === syndication.syndicator_id);
    setEditingSyndication({ ...syndication, ...allocationData });
    setSyndicationForm({ syndicator_id: syndication.syndicator_id, ownership_percentage: syndication.ownership_percentage.toString(), role: syndication.role || 'INVESTOR' });
    setSyndicationDialogOpen(true);
  };

  // 10/07 (Josh, deal 10965): when PFS already holds a position, the unsyndicated
  // remainder (rounding dust like 0.01%) is shown ON that row instead of as a
  // separate "retained — house share" line. Display-only — the stored
  // ownership / advance / target are untouched and nothing is written back.
  const totalOwnedPct = syndications.reduce((s, x) => s + (x.ownership_percentage || 0), 0);
  const retainedPct = +(100 - totalOwnedPct).toFixed(2);
  const pfsPositionId = syndications.find(s =>
    isPfsName(s.syndicator_name) || isPfsName((investors || []).find(i => i.id === s.syndicator_id)?.company_name)
  )?.syndicator_id || null;
  const foldRetainedIntoPfs = retainedPct > 0.01 && !!pfsPositionId;

  const mergedSyndications = syndications.map(syn => {
    const alloc = allocations?.allocations?.find(a => a.syndicator_id === syn.syndicator_id);
    const retained = (foldRetainedIntoPfs && syn.syndicator_id === pfsPositionId) ? {
      retained_pct: retainedPct,
      retained_advance: (dealFinancials?.funding_amount || 0) * retainedPct / 100,
      retained_target: (dealFinancials?.payback || 0) * retainedPct / 100,
    } : {};
    return {
      ...syn,
      ...retained,
      available_base: alloc?.available_base ?? 0, available_fees: alloc?.available_fees ?? 0,
      available_total: alloc?.available_total ?? 0, withdrawn: alloc?.withdrawn ?? 0,
      blocked_reason: alloc?.blocked_reason ?? null, is_stale: alloc?.is_stale ?? false,
      stale_reason: alloc?.stale_reason ?? null, last_computed: alloc?.last_computed ?? null,
      collect_fees: alloc?.collect_fees ?? false, fee_allocated: alloc?.fee_allocated ?? 0,
      running_collection: alloc?.running_collection ?? syn.running_collection ?? 0,
      fees_collected: alloc?.fees_collected ?? syn.fees_collected ?? 0,
      balance: alloc?.balance ?? syn.balance ?? 0,
      target_collection: alloc?.target_collection ?? syn.target_collection ?? 0
    };
  });

  const pools = allocations?.pools || { base_pool: 0, fees_pool: 0, extras_pool: 0 };
  const isStale = allocations?.syndication_stale || mergedSyndications.some(s => s.is_stale);

  return (
    <div className="space-y-4" data-testid="syndication-tab">
      <SyndicationSummary pools={pools} allocations={allocations} isStale={isStale} isAdmin={isAdmin} recomputing={recomputing} onRecompute={handleRecompute} />
      {isAdmin && <CofundedOwedCard dealId={dealId} getAuthHeaders={getAuthHeaders} />}

      {(() => {
        // PFS retains any un-syndicated remainder, so under-100% is COMPLETE.
        // Only flag genuine over-allocation.
        const totalPct = (syndications || []).reduce((s, x) => s + (x.ownership_percentage || 0), 0);
        if (!syndications || syndications.length === 0 || totalPct <= 100.01) return null;
        return (
          <div className="rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800" data-testid="syndication-incomplete-banner">
            <span className="font-semibold">Syndication over-allocated:</span> allocations total {totalPct.toFixed(2)}% —{' '}
            <button
              type="button"
              onClick={() => setSyndicationDialogOpen(true)}
              className="underline font-semibold text-red-900 hover:text-red-700"
              data-testid="syndication-incomplete-fix-link"
            >
              fix this deal's syndication
            </button>
          </div>
        );
      })()}

      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="flex items-center gap-3">
              Syndication Partners
              {(() => {
                if (!syndications || syndications.length === 0) return null;
                const totalPct = syndications.reduce((s, x) => s + (x.ownership_percentage || 0), 0);
                const over = totalPct > 100.01;
                const pfsPct = Math.max(0, 100 - totalPct);
                return (
                  <span
                    className={`text-sm font-semibold px-2.5 py-0.5 rounded-full border ${over ? 'bg-red-50 text-red-700 border-red-300' : 'bg-green-50 text-green-700 border-green-300'}`}
                    data-testid="syndication-total-badge"
                  >
                    {over
                      ? `Syndication = ${+totalPct.toFixed(2)}%`
                      : pfsPct > 0.01
                        ? `Syndication = 100% (${+totalPct.toFixed(2)}% syndicated + ${+pfsPct.toFixed(2)}% PFS)`
                        : 'Syndication = 100%'}
                  </span>
                );
              })()}
            </CardTitle>
            {isAdmin && (
              <Dialog open={syndicationDialogOpen} onOpenChange={(open) => {
                setSyndicationDialogOpen(open);
                if (!open) { setEditingSyndication(null); setCart([]); setCartForm({ syndicator_id: '', ownership_percentage: '', role: 'INVESTOR' }); }
              }}>
                <DialogTrigger asChild>
                  <Button size="sm" data-testid="add-syndicator-btn" onClick={() => { setEditingSyndication(null); setCart([]); }}><Plus className="h-4 w-4 mr-2" />Add Syndicators</Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-2xl">
                  {editingSyndication ? (
                    <>
                      <DialogHeader><DialogTitle>Edit Syndicator</DialogTitle></DialogHeader>
                      <form onSubmit={onAddSyndication} className="space-y-4">
                        <div><Label>Syndicator/Investor</Label><Select value={syndicationForm.syndicator_id} onValueChange={(value) => setSyndicationForm({...syndicationForm, syndicator_id: value})} required><SelectTrigger data-testid="syndicator-select"><SelectValue placeholder="Select investor" /></SelectTrigger><SelectContent>{investors.map((inv) => <SelectItem key={inv.id} value={inv.id}>{inv.company_name}</SelectItem>)}</SelectContent></Select></div>
                        <div><Label>% Invested</Label><Input type="number" step="0.01" min="0" max="100" value={syndicationForm.ownership_percentage} onChange={(e) => setSyndicationForm({...syndicationForm, ownership_percentage: e.target.value})} required data-testid="ownership-input" /></div>
                        <div><Label>Role</Label><Select value={syndicationForm.role || 'INVESTOR'} onValueChange={(value) => setSyndicationForm({...syndicationForm, role: value})}><SelectTrigger data-testid="edit-role-select"><SelectValue placeholder="Select role" /></SelectTrigger><SelectContent>{Object.entries(ROLE_LABELS).map(([value, label]) => <SelectItem key={value} value={value}>{label}</SelectItem>)}</SelectContent></Select><p className="text-xs text-gray-500 mt-1">Changing the role recomputes this position's wire-in and target from the deal terms.</p></div>
                        <div className="flex justify-between">
                          {onDeleteSyndication && <Button type="button" variant="destructive" onClick={() => onDeleteSyndication(editingSyndication.id)} data-testid="delete-syndication-btn">Delete</Button>}
                          <div className="flex space-x-2 ml-auto"><Button type="button" variant="outline" onClick={() => { setSyndicationDialogOpen(false); setEditingSyndication(null); }}>Cancel</Button><Button type="submit" data-testid="save-syndication-btn">Update Syndicator</Button></div>
                        </div>
                      </form>
                    </>
                  ) : (
                    <CartDialog investors={investors} syndications={syndications} cart={cart} setCart={setCart} cartForm={cartForm} setCartForm={setCartForm} saving={saving} onCheckout={handleCartCheckout} onClose={() => setSyndicationDialogOpen(false)} dealFinancials={dealFinancials} />
                  )}
                </DialogContent>
              </Dialog>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <SyndicatorList mergedSyndications={mergedSyndications} loading={loading} onEditSyndication={handleEditSyndication} dealId={dealId}
                          getAuthHeaders={getAuthHeaders} onChanged={() => { if (onRefetch) onRefetch(); }} />
          {(() => {
            // PFS (retained) — display-only; NEVER written to db.syndications.
            // Shown only when PFS has NO position of its own; otherwise the
            // remainder is folded onto PFS's row (see mergedSyndications).
            if (!syndications || syndications.length === 0) return null;
            if (foldRetainedIntoPfs) return null;
            const pfsPct = retainedPct;
            if (pfsPct <= 0.01) return null;
            const funding = dealFinancials?.funding_amount || 0;
            const payback = dealFinancials?.payback || 0;
            const fmt = (n) => n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
            return (
              <div className="flex items-center justify-between px-4 py-2.5 border-t bg-slate-50 text-sm" data-testid="pfs-retained-row">
                <span className="font-medium text-slate-700">
                  Power Funding Solutions <span className="italic text-slate-500">(retained — house share)</span>
                </span>
                <span className="font-mono text-slate-600">
                  {+pfsPct.toFixed(2)}%
                  {funding > 0 && ` · $${fmt(funding * pfsPct / 100)} advance`}
                  {payback > 0 && ` · $${fmt(payback * pfsPct / 100)} target`}
                </span>
              </div>
            );
          })()}
        </CardContent>
      </Card>

      {isAdmin && mergedSyndications.length > 0 && (
        <DistributionTable dealId={dealId} getAuthHeaders={getAuthHeaders} mergedSyndications={mergedSyndications} />
      )}
    </div>
  );
};

export default SyndicationTab;
